import styled from 'styled-components'
import { orderBy } from 'lodash'

import { formatNumber } from '../../utils/numbers'
import { translate } from '../../utils/translation'
import { fonts, palette } from '../../styles/variables'
import { LoadingResponsive } from '../loading/Loading'
import noContent from '../../styles/assets/tableIcons/no_content.svg'
import plusOrange from '../../styles/assets/tableIcons/circle_plus.svg'
import plusGray from '../../styles/assets/tableIcons/circle_plus_grey.svg'
import plusOutline from '../../styles/assets/tableIcons/circle_plus_outline.svg'
import { Text } from '../topography'
import { VALID_STATUS } from '../input/TagInput/CompetitorTagInput'

import { NoDataIcon } from './styled'

const NoDataDiv = styled.div({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
})

const Title = styled.div({
  fontFamily: fonts.primaryBook,
  fontSize: '14px',
  margin: '10px 0',
})

const CompetitorList = styled.div((props) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  overflow: 'auto',
  height: props.height || 'auto',
  position: 'relative',
  minHeight: '200px',
  padding: '16px',
  background: palette.white,
  border: `1px solid ${palette.lightGray}`,
  boxSizing: 'border-box',
  borderRadius: '2px',
}))

const PlusIcon = styled.div((props) => ({
  width: '20px',
  height: '20px',
  background: `url(${props.disable ? plusGray : plusOrange})`,
  backgroundSize: 'contain',
}))

const CompetitorBox = styled.div((props) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px',
  width: '100%',
  minHeight: '72px',
  background: props.disable ? palette.lightGray30 : palette.white,
  border: `1px solid ${palette.lightGray}`,
  boxSizing: 'border-box',
  borderRadius: '2px',
  margin: '8px 0px',
  cursor: 'pointer',
  pointerEvents: props.disable ? 'none' : 'all',
  ':hover': {
    background: palette.lightGray30,
    '.competitorbox-competitor-info': {
      color: props.disable ? palette.darkGray20 : palette.darkGray,
      '& b': {
        color: props.disable ? palette.darkGray20 : palette.darkGray,
      },
    },
    '.competitorbox-plus-icon': {
      background: `url(${props.disable ? plusGray : plusOutline})`,
      backgroundSize: 'contain',
    },
  },
  '.competitorbox-plus-icon': {
    background: `url(${props.disable ? plusGray : plusOrange})`,
    backgroundSize: 'contain',
  },
  ':first-child': {
    marginTop: 0,
  },
  ':last-child': {
    marginBottom: 0,
  },
}))

const CompetitorInfo = styled.div({})
const CompetitorInfoRow = styled.div({ display: 'flex' })

const CompetitorListRender = ({
  pendingData,
  suggestedCompetitors,
  selectedCompetitors,
  handleAddCompetitor,
}) => {
  if (
    pendingData &&
    (!suggestedCompetitors || suggestedCompetitors?.length === 0)
  ) {
    return <LoadingResponsive offsetY="15%" />
  }

  if (suggestedCompetitors?.length === 0) {
    return (
      <NoDataDiv>
        <NoDataIcon src={noContent} />
        {translate('no_competitors_available')}
      </NoDataDiv>
    )
  }

  return orderBy(
    suggestedCompetitors,
    ['gapKeywordCount', 'traffic'],
    ['asc', 'desc'],
  ).map((item) => {
    const exist = selectedCompetitors.some(
      (addedCompetitor) => addedCompetitor.domain === item.domain,
    )
    return (
      <CompetitorBox
        key={item.domain}
        data-testid="ranked-competitors-item"
        onClick={() =>
          handleAddCompetitor({
            domain: item.domain,
            status: VALID_STATUS.valid,
          })
        }
        disable={exist}
      >
        <CompetitorInfo>
          <Text
            className="competitorbox-competitor"
            lineHeight="14px"
            fontFamily={fonts.primaryBook}
            color={exist ? palette.darkGray30 : palette.darkGray}
          >
            {item.domain}
          </Text>
          <CompetitorInfoRow style={{ margin: '6px 0 0 0' }}>
            <Text
              className="competitorbox-competitor-info"
              fontSize="12px"
              lineHeight="18px"
              color={exist ? palette.darkGray20 : palette.darkGray70}
              boldColor={exist ? palette.darkGray20 : palette.darkGray70}
            >
              {translate('competitor_common_kw')}
            </Text>
            <Text
              className="competitorbox-competitor-info"
              fontSize="12px"
              lineHeight="18px"
              fontFamily={fonts.primaryBook}
              color={exist ? palette.darkGray20 : palette.darkGray70}
              boldColor={exist ? palette.darkGray20 : palette.darkGray70}
              style={{ marginLeft: '5px' }}
            >
              {Math.round(item.commonKeywordCount)}
            </Text>
          </CompetitorInfoRow>
          <CompetitorInfoRow>
            <Text
              className="competitorbox-competitor-info"
              fontSize="12px"
              lineHeight="18px"
              color={exist ? palette.darkGray20 : palette.darkGray70}
              boldColor={exist ? palette.darkGray20 : palette.darkGray70}
            >
              {translate('competitor_monthly_traffic')}
            </Text>
            <Text
              className="competitorbox-competitor-info"
              fontSize="12px"
              lineHeight="18px"
              fontFamily={fonts.primaryBook}
              color={exist ? palette.darkGray20 : palette.darkGray70}
              boldColor={exist ? palette.darkGray20 : palette.darkGray70}
              style={{ marginLeft: '5px' }}
            >
              {formatNumber(item.traffic)}
            </Text>
          </CompetitorInfoRow>
        </CompetitorInfo>
        <PlusIcon className="competitorbox-plus-icon" />
      </CompetitorBox>
    )
  })
}

export default ({
  pendingData,
  suggestedCompetitors,
  selectedCompetitors,
  handleAddCompetitor,
  width = '100%',
  height = '300px',
}) => (
  <div style={{ width }}>
    <Title>{translate('add_competitor_ranking')}</Title>
    <CompetitorList data-testid="ranked-competitors-list" height={height}>
      <CompetitorListRender
        pendingData={pendingData}
        suggestedCompetitors={suggestedCompetitors}
        selectedCompetitors={selectedCompetitors}
        handleAddCompetitor={handleAddCompetitor}
      />
    </CompetitorList>
  </div>
)
